import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, SessionCreateResponse, ChatSession } from '../types';
import config from '../utils/config';

// NFC Routing Service URL - Using direct Cloud Run URL until SSL is ready
const NFC_ROUTING_URL = 'https://nfc-routing-docker-wusgso4mxq-uc.a.run.app';

interface NFCResolutionResponse {
  restaurantId: string;
  tableNumber: string;
  serverId?: string;
  status?: string;
}

interface NFCRoutingApiResponse {
  success: boolean;
  data: NFCResolutionResponse;
  cached?: boolean;
  requestId?: string;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(`API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.error('API Response Error:', error.response?.data || error.message);
        
        // Handle common HTTP errors
        if (error.response?.status === 429) {
          throw new Error('Too many requests. Please wait a moment and try again.');
        }
        
        if (error.response?.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        
        if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
        }
        
        throw new Error(error.message || 'Network error');
      }
    );
  }

  /**
   * Resolve NFC ID to restaurant and table information
   */
  async resolveNFC(nfcId: string): Promise<NFCResolutionResponse> {
    try {
      const response = await axios.post<NFCRoutingApiResponse>(
        `${NFC_ROUTING_URL}/resolve-nfc`,
        { nfc_id: nfcId },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error('Invalid response from NFC routing service');
      }

      const nfcData = response.data.data;
      
      // Map the response to the expected format
      return {
        restaurantId: nfcData.restaurantId,
        tableNumber: nfcData.tableNumber,
        serverId: nfcData.serverId,
        status: 'active'
      };
    } catch (error) {
      console.error('NFC resolution error:', error);
      throw new Error('Unable to resolve NFC tag. Please try scanning again.');
    }
  }

  /**
   * Create a new chat session using NFC ID
   */
  async createSessionFromNFC(
    nfcId: string,
    patronToken: string
  ): Promise<SessionCreateResponse> {
    try {
      // First, resolve the NFC ID to get restaurant and table info
      const nfcData = await this.resolveNFC(nfcId);
      
      // Then create the session
      const response = await this.client.post<ApiResponse<SessionCreateResponse>>(
        '/api/sessions',
        {
          restaurantId: nfcData.restaurantId,
          nfcId: nfcId,
          patronToken: patronToken
        }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Failed to create session');
      }

      return response.data.data;
    } catch (error) {
      console.error('Create session from NFC error:', error);
      throw error;
    }
  }

  /**
   * Legacy: Create a new chat session (for backward compatibility)
   */
  async createSession(
    restaurantId: string,
    tableNumber: string,
    patronToken: string
  ): Promise<SessionCreateResponse> {
    try {
      const response = await this.client.post<ApiResponse<SessionCreateResponse>>(
        '/api/sessions',
        {
          restaurantId,
          tableNumber,
          patronToken
        }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Failed to create session');
      }

      return response.data.data;
    } catch (error) {
      console.error('Create session error:', error);
      throw error;
    }
  }

  /**
   * Get session details
   */
  async getSession(sessionId: string): Promise<ChatSession> {
    try {
      const response = await this.client.get<ApiResponse<ChatSession>>(
        `/api/sessions/${sessionId}`
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Session not found');
      }

      // Convert date strings to Date objects
      const sessionData = response.data.data;
      return {
        ...sessionData,
        createdAt: new Date(sessionData.createdAt),
        expiresAt: new Date(sessionData.expiresAt)
      };
    } catch (error) {
      console.error('Get session error:', error);
      throw error;
    }
  }

  /**
   * Get session by hash (for hash-based URLs)
   */
  async getSessionByHash(sessionHash: string): Promise<any> {
    try {
      const response = await fetch(`https://nfc-session-docker-wusgso4mxq-uc.a.run.app/api/sessions/by-hash/${sessionHash}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success || !data.session) {
        throw new Error(data.message || 'Session not found');
      }

      return data.session;
    } catch (error) {
      console.error('Error getting session by hash:', error);
      throw error;
    }
  }

  /**
   * Close a session
   */
  async closeSession(sessionId: string): Promise<void> {
    try {
      const response = await this.client.delete<ApiResponse<void>>(
        `/api/sessions/${sessionId}`,
        {
          data: { closedBy: 'patron' }
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to close session');
      }
    } catch (error) {
      console.error('Close session error:', error);
      throw error;
    }
  }

  /**
   * Update session activity (extend timeout)
   */
  async updateSessionActivity(sessionId: string): Promise<void> {
    try {
      const response = await this.client.put<ApiResponse<void>>(
        `/api/sessions/${sessionId}/activity`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update session activity');
      }
    } catch (error) {
      console.error('Update session activity error:', error);
      // Don't throw for activity updates - they're not critical
      console.warn('Session activity update failed, continuing...');
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.client.get('/health');
      return response.status === 200;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  /**
   * Get restaurant information (if endpoint exists)
   */
  async getRestaurant(restaurantId: string): Promise<any> {
    try {
      const response = await this.client.get(`/api/restaurants/${restaurantId}`);
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Restaurant not found');
      }

      return response.data.data;
    } catch (error) {
      console.error('Get restaurant error:', error);
      // Return default restaurant data if API doesn't exist yet
      return {
        id: restaurantId,
        name: 'Restaurant',
        logoUrl: null,
        address: ''
      };
    }
  }

  /**
   * Get waiter information (if endpoint exists)
   */
  async getWaiter(waiterId: string): Promise<any> {
    try {
      const response = await this.client.get(`/api/waiters/${waiterId}`);
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Waiter not found');
      }

      return response.data.data;
    } catch (error) {
      console.error('Get waiter error:', error);
      // Return default waiter data if API doesn't exist yet
      return {
        id: waiterId,
        name: 'Your Server',
        photoUrl: null,
        status: 'active'
      };
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;
